import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { keepingNavState } from '../utils/searchParamsNavigation';
import axios from 'axios';
import { environment } from '../../environments/environment';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { Menu, MenuItem } from '@mui/material';
import CapsuleInsightsCard from './Insights/CapsuleInsightsCard';
import InsightGroupDrawer, { InsightSource, OpenInsight, ReasoningTarget } from './Insights/InsightGroupDrawer';
import { GROUP_ACTIVITY_PARAM, GROUP_NAME_PARAM, GROUP_PARAM, GROUP_PARAMS, withReasoningFor } from './Insights/reasoningLink';
import { clearStoredAuthTokens } from '../utils/auth';
import WorkflowVisualization from './WorkflowVisualization';

import Sidebar from './Sidebar';
import ChatAssistance, { AgentReference, chatColumnStyle, useChatPanel } from './ChatAssistance';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';
import RunHealthPanel from './ExecutionStats/RunHealthPanel';
import StageCard from './Explainability/StageCard';
import StageDetailDrawer from './Explainability/StageDetailDrawer';
import { ExplainabilityDataElement } from './Explainability/explainabilityTypes';
import { explainabilityService } from '../services/explainabilityService';
import { dsVar, ink } from '../theme/tokens';

// Max time to wait for a reasoning-log websocket message before giving up.
// Acts as an idle safety cap only — normal completion is driven by the flow's
// terminal status ("Success"/"Failure"); the socket keeps waiting across long
// inter-stage gaps while the status is still "Executing".
const REASONING_IDLE_TIMEOUT_MS = 600000; // 10 minutes

const INSIGHTS_TABS = ['Insights', 'Run health', 'Explainability'];

/** The tab named by the query string's slug ("run-health"); anything unrecognised opens on Insights. */
const tabFromParams = (params: URLSearchParams): string => {
    const requested = (params.get('tab') || '').replace(/-/g, ' ');
    return INSIGHTS_TABS.some(tab => tab.toLowerCase() === requested) ? requested : 'insights';
};

const CampaignInsights = () => {
    const { projectId, campaignId } = useParams<{ projectId: string; campaignId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [explainabilityLoading, setExplainabilityLoading] = useState(false);
    const [explainabilityError, setExplainabilityError] = useState<string | null>(null);
    const [explainabilityAttempt, setExplainabilityAttempt] = useState(0);
    // data state is now managed inside each CapsuleExpandableCard
    const [explainabilityData, setExplainabilityData] = useState<ExplainabilityDataElement[]>([]);
    const [selectedStageIndex, setSelectedStageIndex] = useState(0);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [campaignName, setCampaignName] = useState<string>((location.state as any)?.campaign_name || '');
    const [searchParams, setSearchParams] = useSearchParams();
    /**
     * The tab, kept in the query string as a slug ("run-health") so a link can
     * open one directly — the project page's workflow drawer links straight to
     * Run health. Anything unrecognised opens on Insights.
     */
    const [activeTab, setActiveTab] = useState(() => tabFromParams(searchParams));
    // Back from an Insights row's "See full reasoning" pushes the tab back through the URL.
    useEffect(() => {
        const requested = tabFromParams(searchParams);
        setActiveTab(current => (current === requested ? current : requested));
    }, [searchParams]);
    /**
     * The Insights group whose reasoning the Explainability tab is narrowed to, kept
     * in the query string so a row's "See full reasoning" link can be shared.
     */
    const reasoningGroup = searchParams.get(GROUP_PARAM);
    const reasoningGroupName = searchParams.get(GROUP_NAME_PARAM);
    const reasoningActivity = searchParams.get(GROUP_ACTIVITY_PARAM);
    const selectTab = (tab: string) => {
        setActiveTab(tab);
        setOpenGroup(null);
        setOpenStageIndex(null);
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab.replace(/ /g, '-'));
        if (tab !== 'explainability') GROUP_PARAMS.forEach(key => next.delete(key));
        // keepingNavState: campaign_name arrives in location.state.
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    };
    /**
     * Opens the Explainability tab on one Insights group's reasoning. Pushed, so Back returns to the row.
     * Only this campaign's: an assistant reference to another campaign keeps its reasoning summary in the
     * drawer, but this page's tab cannot show it.
     */
    const reasoningActionFor = (target: ReasoningTarget) => target.campaignId !== campaignId ? undefined : () => {
        setOpenGroup(null);
        setOpenStageIndex(null);
        setSearchParams(withReasoningFor(searchParams, target), keepingNavState(location.state));
    };
    const showWholeFlow = () => {
        const next = new URLSearchParams(searchParams);
        GROUP_PARAMS.forEach(key => next.delete(key));
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    };
    const [capsules, setCapsules] = useState<any[]>([]);
    const [expandedCapsuleId, setExpandedCapsuleId] = useState<string | null>(null);
    const [flowTemplateCode, setFlowTemplateCode] = useState<string>('');
    const [operatorCode, setOperatorCode] = useState<string>('');
    const [isCustomizing, setIsCustomizing] = useState(false);
    // The summary row, or assistant reference, whose detail is open in the drawer.
    const [openGroup, setOpenGroup] = useState<OpenInsight | null>(null);
    // Whether the open drawer, on either tab, is at its wide width. It holds while moving
    // between rows or stages, and resets once nothing is open.
    const [drawerWide, setDrawerWide] = useState(false);
    const toggleDrawerWide = useCallback(() => setDrawerWide(wide => !wide), []);
    const chatPanel = useChatPanel(Boolean(openGroup), drawerWide && Boolean(openGroup));
    const { newConversation } = chatPanel;
    // Only Insights renders the assistant, so leaving the tab drops its messages; the
    // conversation goes with them, rather than the agent carrying turns back that the
    // user can no longer see.
    useEffect(() => {
        if (activeTab !== 'insights') newConversation();
    }, [activeTab, newConversation]);
    // The explainability stage whose observations and decisions are open in the drawer.
    const [openStageIndex, setOpenStageIndex] = useState<number | null>(null);
    const drawerOpen = Boolean(openGroup) || openStageIndex !== null;
    useEffect(() => {
        if (!drawerOpen) setDrawerWide(false);
    }, [drawerOpen]);
    // Per-capsule counter; bumping it refetches that capsule's summary page.
    const [summaryRefresh, setSummaryRefresh] = useState<Record<string, number>>({});

    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [interactionId, setInteractionId] = useState<string | null>(null);
    const [reasoningLogs, setReasoningLogs] = useState<any[]>([]);
    const [analysisError, setAnalysisError] = useState<string | null>(null);

    // Fetch capsules on mount
    useEffect(() => {
        const fetchCapsules = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${environment.controlplaneUrl}/manage/capsules`,
                    {
                        params: { campaignId, page: 1, limit: 20, persistOutput: true },
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        }
                    }
                );
                const items: any[] = Array.isArray(response.data)
                    ? response.data
                    : response.data?.data ?? response.data?.capsules ?? [];
                setCapsules(items);
                if (items.length > 0) {
                    setExpandedCapsuleId(items[items.length - 1]._id);
                }
            } catch (error) {
                console.error("Failed to fetch capsules:", error);
            } finally {
                setLoading(false);
            }
        };

        if (campaignId) {
            fetchCapsules();
        }
    }, [campaignId]);

    useEffect(() => {
        if (activeTab === 'insights' && capsules.length > 0 && !expandedCapsuleId) {
            setExpandedCapsuleId(capsules[capsules.length - 1]._id);
        }
    }, [activeTab, capsules, expandedCapsuleId]);

    // Fetch campaign details on campaign change
    useEffect(() => {
        if (!campaignId) return;
        let cancelled = false;

        const fetchCampaignDetails = async () => {
            try {
                const response = await axios.get(
                    `${environment.controlplaneUrl}/manage/campaigns/${campaignId}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                if (!cancelled && response.data && response.data.campaign) {
                    const campaign = response.data.campaign;
                    setCampaignName(campaign.campaign_name);

                    // Prefer the campaign's own flow_template_code when present
                    // (older campaigns carry it directly). Only fall back to the
                    // operator record when the campaign has no code of its own.
                    const campaignOperatorCode = campaign.operator_code;
                    if (!cancelled) setOperatorCode(campaignOperatorCode || '');
                    let resolvedCode = campaign.flow_template_code || '';
                    if (!resolvedCode && campaignOperatorCode) {
                        try {
                            const opRes = await axios.get(
                                `${environment.controlplaneUrl}/manage/operators/${campaignOperatorCode}`,
                                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                            );
                            resolvedCode =
                                opRes.data?.operator?.flow_template_code ??
                                opRes.data?.flow_template_code ??
                                resolvedCode;
                        } catch (opError) {
                            if (!cancelled) console.error("Failed to fetch operator details:", opError);
                        }
                    }
                    if (!cancelled) setFlowTemplateCode(resolvedCode);
                }
            } catch (error) {
                if (!cancelled) console.error("Failed to fetch campaign details:", error);
            }
        };

        fetchCampaignDetails();
        return () => { cancelled = true; };
    }, [campaignId]);

    // Fetch explainability data when the Explainability tab is active: the whole
    // flow, or one Insights group's reasoning when the tab is narrowed to it.
    useEffect(() => {
        if (activeTab !== 'explainability' || !campaignId) return;
        const controller = new AbortController();
        setExplainabilityLoading(true);
        setExplainabilityError(null);
        setExplainabilityData([]);
        setOpenStageIndex(null);
        explainabilityService
            .getStageContext(
                { campaignId, groupIdentifier: reasoningGroup, activityTemplateCode: reasoningActivity },
                { signal: controller.signal }
            )
            .then(setExplainabilityData)
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.error("Failed to fetch explainability data:", error);
                setExplainabilityError(error?.response?.data?.error || 'Could not load the reasoning for this campaign.');
            })
            .finally(() => { if (!controller.signal.aborted) setExplainabilityLoading(false); });
        return () => controller.abort();
    }, [activeTab, campaignId, reasoningGroup, reasoningActivity, explainabilityAttempt]);

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };

    const handleSignOut = () => {
        clearStoredAuthTokens();
        handleUserMenuClose();
        navigate('/login');
    };

    const generateInteractionId = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // WebSocket Effect
    useEffect(() => {
        if (!isAnalyzing || !interactionId) return;

        let wsUrl = (environment.controlplaneUrl || '').replace(/^http/, 'ws');
        wsUrl = `${wsUrl}/ws/reasoning-logs?interactionId=${interactionId}`;

        const ws = new WebSocket(wsUrl);
        let timeoutId: ReturnType<typeof setTimeout>;

        let finalized = false;

        const fetchCapsules = async (): Promise<any[]> => {
            const response = await axios.get(
                `${environment.controlplaneUrl}/manage/capsules`,
                {
                    params: { campaignId, page: 1, limit: 20, persistOutput: true },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    }
                }
            );
            return Array.isArray(response.data)
                ? response.data
                : response.data?.data ?? response.data?.capsules ?? [];
        };

        // Terminate the loader and the socket. Called on a terminal flow status
        // ("Success"/"Failure") or when the idle safety cap is hit (timedOut).
        const finalizeAnalysis = async (opts: { status?: string; timedOut?: boolean }) => {
            if (finalized) return;
            finalized = true;
            if (timeoutId) clearTimeout(timeoutId);
            try {
                const items = await fetchCapsules();
                if (items.length > 0) {
                    setCapsules(items);
                    setExpandedCapsuleId(items[items.length - 1]._id);
                }
                if (opts.status === 'failure') {
                    setAnalysisError("The analysis run reported a failure. Please review and try again.");
                } else if (opts.timedOut && items.length === 0) {
                    setAnalysisError("Analysis is taking longer than expected and timed out. Please try again.");
                }
            } catch (error) {
                console.error("Failed to fetch capsules on finalize:", error);
                if (opts.timedOut) setAnalysisError("Analysis timed out. Please try again.");
            } finally {
                setIsAnalyzing(false);
                ws.close();
            }
        };

        const resetTimeout = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                finalizeAnalysis({ timedOut: true });
            }, REASONING_IDLE_TIMEOUT_MS);
        };

        ws.onopen = () => {
            console.log('WebSocket connected for interactionId:', interactionId);
            resetTimeout();
        };

        ws.onmessage = (event) => {
            resetTimeout();
            try {
                const data = JSON.parse(event.data);
                console.log("WebSocket message:", data);

                if (data.reasonings && Array.isArray(data.reasonings)) {
                    setReasoningLogs(data.reasonings);
                } else if (data.fullDocument && data.fullDocument.reasonings && Array.isArray(data.fullDocument.reasonings)) {
                    setReasoningLogs(data.fullDocument.reasonings);
                } else if (Array.isArray(data)) {
                    setReasoningLogs(data);
                } else {
                    setReasoningLogs(prev => [...prev, data]);
                }

                // Only conclude the run on a terminal status. While "Executing",
                // keep the socket open (resetTimeout above extends the idle cap)
                // so long gaps between stages don't tear the screen down early.
                const status = String(data?.fullDocument?.status ?? data?.status ?? '').toLowerCase();
                if (status === 'success' || status === 'failure') {
                    finalizeAnalysis({ status });
                }
            } catch (err) {
                console.error("Error parsing websocket message:", err);
                setReasoningLogs(prev => [...prev, event.data]);
            }
        };

        ws.onerror = (error) => {
            console.error("WebSocket error:", error);
        };

        ws.onclose = () => {
            console.log('WebSocket connection closed.');
            if (timeoutId) clearTimeout(timeoutId);
        };

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            ws.close();
        };
    }, [isAnalyzing, interactionId, campaignId]);

    // Stable, so the docked drawer's Escape listener isn't re-bound every render.
    const closeGroup = useCallback(() => setOpenGroup(null), []);
    const openReference = useCallback((ref: AgentReference) => setOpenGroup({ kind: 'insight', ...ref }), []);
    const refreshAfterReview = (source: InsightSource) => {
        // A decision on another campaign's insight changes nothing listed here.
        if (source.campaignId && source.campaignId !== campaignId) return;
        setSummaryRefresh(prev => {
            // Without a capsule named, every capsule's summary is refetched.
            const capsuleIds: string[] = source.capsuleId ? [source.capsuleId] : capsules.map(c => c._id);
            const next = { ...prev };
            capsuleIds.forEach(id => { next[id] = (prev[id] ?? 0) + 1; });
            return next;
        });
    };
    const closeStage = useCallback(() => setOpenStageIndex(null), []);

    const renderExplainability = () => {
        const agentYielded = drawerWide && openStageIndex !== null;
        const groupLabel = reasoningGroupName || reasoningGroup;
        const groupBanner = reasoningGroup && (
            <div className="flex items-center justify-between gap-4 bg-surface border border-accent-fg/30 rounded-xl px-4 py-3" style={{ fontFamily: '"Inter", sans-serif' }}>
                <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-accent-fg/80 m-0">Reasoning for one Insights row</p>
                    <p className="text-sm text-fg m-0 truncate" title={groupLabel ?? undefined}>
                        {groupLabel}
                        {reasoningGroupName && reasoningGroupName !== reasoningGroup && (
                            <span className="text-fg-muted font-mono text-xs"> · {reasoningGroup}</span>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={showWholeFlow}
                    className="shrink-0 text-xs font-semibold text-fg-soft hover:text-fg bg-transparent border border-line hover:border-fg-muted rounded-lg px-3 py-1.5 cursor-pointer"
                >
                    Show the whole flow
                </button>
            </div>
        );

        let stages: React.ReactNode;
        if (explainabilityLoading) {
            stages = (
                <div className="flex flex-col gap-4">
                    <div className="h-32 bg-surface rounded-xl animate-pulse"></div>
                    <div className="h-32 bg-surface rounded-xl animate-pulse"></div>
                </div>
            );
        } else if (explainabilityError) {
            stages = (
                <div className="flex flex-col items-center justify-center gap-3 h-64 border border-dashed border-red-500/40 rounded-xl" style={{ fontFamily: '"Inter", sans-serif' }}>
                    <p className="text-red-300 text-sm m-0">{explainabilityError}</p>
                    <button type="button" onClick={() => setExplainabilityAttempt(n => n + 1)} className="text-xs font-semibold text-fg-soft hover:text-fg bg-transparent border border-line rounded-lg px-3 py-1.5 cursor-pointer">
                        Try again
                    </button>
                </div>
            );
        } else if (explainabilityData.length === 0) {
            stages = (
                <div className="flex flex-col items-center justify-center gap-2 h-64 border border-dashed border-line rounded-xl px-6 text-center" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {reasoningGroup ? (
                        <>
                            <p className="text-fg-soft text-sm m-0">No reasoning was recorded for this row.</p>
                            <p className="text-fg-muted text-xs m-0 max-w-md">
                                Reasoning is linked to Insights rows only for runs recorded since that was added. It will appear here after the campaign next runs.
                            </p>
                        </>
                    ) : (
                        <p className="text-fg-muted m-0">No explainability data available for this campaign.</p>
                    )}
                </div>
            );
        } else {
            stages = explainabilityData.map((element, index) => (
                <StageCard
                    key={element.node_execution_id ?? index}
                    stage={element}
                    position={index + 1}
                    selected={openStageIndex === index}
                    onOpen={() => setOpenStageIndex(index)}
                />
            ));
        }

        return (
            <div className={`flex h-full min-h-[calc(100vh-200px)] ${agentYielded ? 'gap-0' : 'gap-8'}`}>
                {/* Left Side: Investigation Agent, which steps aside for a widened stage drawer */}
                <div className="shrink-0 h-full flex flex-col" style={chatColumnStyle('350px', agentYielded)}>
                    <ChatAssistance
                        title="Investigation Agent"
                        subtitle="Explainability Analyst"
                        agentCode="INVESTIGATION_AGENT"
                        contextData={{
                            campaignId, projectId, campaignName,
                            ...(reasoningGroup ? { groupIdentifier: reasoningGroup, groupName: reasoningGroupName } : {}),
                        }}
                    />
                </div>

                {/* Right Side: Stage outcomes; each opens its observations and decisions in the drawer */}
                <div className="flex-1 min-w-0 space-y-4 overflow-y-auto pr-2 custom-scrollbar pb-10">
                    {groupBanner}
                    {stages}
                </div>
            </div>
        );
    };

    // Resolves the flow template for the campaign's operator before handing off
    // to the AI Studio editor, which loads the flow from the code in nav state.
    const handleCustomize = async () => {
        if (isCustomizing) return;
        setIsCustomizing(true);
        try {
            let resolvedCode = flowTemplateCode;
            if (operatorCode) {
                const opRes = await axios.get(
                    `${environment.controlplaneUrl}/manage/operators/${operatorCode}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                resolvedCode =
                    opRes.data?.operator?.flow_template_code ??
                    opRes.data?.flow_template_code ??
                    resolvedCode;
            }
            navigate('/AIStudio/customize', {
                state: {
                    flow_template_code: resolvedCode,
                    operator_id: operatorCode,
                    operator_name: campaignName,
                    fromOperator: true,
                    // Carried so the editor's Run button can execute the flow
                    // against this campaign and link back to its results.
                    campaign_id: campaignId,
                    project_id: projectId,
                    returnTo: { pathname: `/analytics/projects/${projectId}/campaign/${campaignId}` }
                }
            });
        } catch (error) {
            console.error("Failed to fetch operator details:", error);
            setAnalysisError("Couldn't open the workflow editor. Please try again.");
        } finally {
            setIsCustomizing(false);
        }
    };

    const QuickConnectFallback = () => {
        const handleConnectAndAnalyze = async () => {
            setAnalysisError(null);
            setIsAnalyzing(true);
            const newInteractionId = generateInteractionId();
            setInteractionId(newInteractionId);
            setReasoningLogs([]);

            const payload = {
                data: {
                    sourceLink: '',
                    sourceName: "User Input",
                    fileType: "docs",
                    contentType: "GENERIC_DATASET",
                    campaignId: campaignId,
                    projectId: projectId
                },
                user: {
                    interactionId: newInteractionId
                },
                agent: {
                    code: flowTemplateCode
                }
            };

            // Stop the loader, tear down the reasoning websocket (the effect's
            // cleanup fires when isAnalyzing flips false; clearing interactionId
            // prevents it from reopening) and surface a message to the user.
            const failAnalysis = (message: string) => {
                setIsAnalyzing(false);
                setInteractionId(null);
                setAnalysisError(message);
            };

            const genericError = "Analysis could not be started. Please try again.";

            try {
                const response = await axios.post(`${environment.controlplaneUrl}/agent/flow/execute`, payload, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                // The endpoint can return an error in the body (e.g. an upstream
                // failure wrapped as { status, error }) without a non-2xx HTTP
                // status, so inspect the payload before starting to wait on logs.
                const body: any = response?.data;
                const statusCode = Number(body?.status);
                const errorMessage = typeof body?.error === 'string' ? body.error.trim() : '';
                if ((Number.isFinite(statusCode) && statusCode >= 400) || errorMessage) {
                    failAnalysis(errorMessage || genericError);
                }
            } catch (error: any) {
                console.error("Failed to execute flow:", error);
                if (error?.response) {
                    // The server actively rejected the request (HTTP 4xx/5xx) —
                    // the flow did not start, so stop and surface the message.
                    const respData = error.response.data;
                    failAnalysis(respData?.error || respData?.message || genericError);
                } else {
                    // No HTTP response (e.g. ERR_NETWORK / aborted request). The
                    // flow may already be running — the reasoning websocket is a
                    // separate channel and may be streaming logs — so do NOT tear
                    // it down here. Let the websocket drive completion, with the
                    // idle timeout as the backstop if nothing ever arrives.
                    console.warn("Execute flow request got no response; keeping the reasoning stream open.");
                }
            }
        };

        if (isAnalyzing) {
            return (
                <div className="flex flex-col gap-6 h-full animate-fadeIn min-h-[500px]">
                    <div className="space-y-3 text-left">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-accent/10 border border-accent-fg/20">
                                <AutoAwesomeIcon style={{ color: dsVar('accent-fg'), fontSize: 24 }} className="animate-pulse" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-fg tracking-tight">Intelligence Engine Active</h2>
                                <div className="flex items-center gap-2 mt-1">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                    <p className="text-[10px] uppercase tracking-[0.2em] font-black text-emerald-500/80" style={{ fontFamily: '"Inter", sans-serif' }}>System Analysis in Progress</p>
                                </div>
                            </div>
                        </div>
                        <p className="text-fg-2 text-sm max-w-2xl leading-relaxed" style={{ fontFamily: '"Inter", sans-serif' }}>
                            We're orchestrating specialized agents to process your source link and extract strategic insights.
                            Your modules will be ready right here once processing is complete.
                        </p>
                    </div>

                    <div className="flex-1 bg-page/80 backdrop-blur-xl border border-line rounded-3xl p-8 overflow-y-auto relative custom-scrollbar shadow-2xl">
                        {/* Subtle scanline effect overlay */}
                        <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.1)_50%),linear-gradient(90deg,rgba(255,0,0,0.02),rgba(0,255,0,0.01),rgba(0,0,255,0.02))] bg-[length:100%_4px,3px_100%] opacity-20"></div>

                        {reasoningLogs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full gap-6">
                                <div className="relative">
                                    <div className="absolute inset-0 rounded-full bg-accent/20 blur-xl animate-pulse"></div>
                                    <CircularProgress size={48} thickness={2} sx={{ color: dsVar('accent-fg') }} />
                                </div>
                                <div className="text-center space-y-2">
                                    <span className="block text-sm font-bold text-fg tracking-wider">BOOTING REASONING CORE</span>
                                    <span className="block text-[11px] text-fg-muted max-w-[280px] leading-relaxed">
                                        Establishing secure connection to analysis swarm. Grab a coffee, this won't take long.
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6 relative">
                                {reasoningLogs.map((log, index) => {
                                    const displayText = log && typeof log === 'object' && log.reasoning
                                        ? log.reasoning
                                        : (typeof log === 'string' ? log : '');

                                    if (!displayText) return null;

                                    return (
                                        <div key={index} className="group flex gap-4 animate-slideIn">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="w-6 h-6 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-none group-hover:bg-emerald-500/20 transition-colors">
                                                    <span className="text-emerald-500 text-[10px] font-bold">✓</span>
                                                </div>
                                                {index !== reasoningLogs.length - 1 && <div className="w-[1px] flex-1 bg-gradient-to-b from-line to-transparent"></div>}
                                            </div>
                                            <div className="flex-1 pt-0.5">
                                                <p className="text-[13px] text-fg-soft font-medium leading-relaxed group-hover:text-fg transition-colors" style={{ fontFamily: '"Inter", sans-serif' }}>
                                                    {displayText}
                                                </p>
                                                <p className="text-[9px] text-fg-muted mt-1 uppercase tracking-widest font-bold opacity-60" style={{ fontFamily: '"Inter", sans-serif' }}>Processed • Step {index + 1}</p>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Active Task Indicator */}
                                <div className="flex gap-4 animate-pulse mt-2">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="w-6 h-6 rounded-lg bg-accent/10 border border-accent-fg/20 flex items-center justify-center flex-none">
                                            <div className="w-1.5 h-1.5 bg-accent rounded-full animate-ping"></div>
                                        </div>
                                    </div>
                                    <div className="flex-1 pt-0.5">
                                        <p className="text-[13px] text-accent-fg font-bold tracking-wide italic" style={{ fontFamily: '"Inter", sans-serif' }}>Executing specialized intelligence routines...</p>
                                        <div className="flex gap-1.5 mt-2 opacity-70">
                                            <div className="w-1 h-1 bg-accent rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                            <div className="w-1 h-1 bg-accent rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                            <div className="w-1 h-1 bg-accent rounded-full animate-bounce"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center gap-6 min-h-full py-10 text-center animate-fadeIn">
                {analysisError && (
                    <div className="w-full max-w-2xl flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-left">
                        <ErrorOutlineIcon style={{ color: ink('#f87171'), fontSize: 20 }} className="mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm font-bold text-red-300 m-0" style={{ fontFamily: '"Inter", sans-serif' }}>Analysis couldn't start</p>
                            <p className="text-xs text-red-200/80 m-0 mt-1" style={{ fontFamily: '"Inter", sans-serif' }}>{analysisError}</p>
                        </div>
                        <button
                            onClick={() => setAnalysisError(null)}
                            aria-label="Dismiss error"
                            className="text-red-300/60 hover:text-red-300 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                        >
                            <CloseIcon fontSize="small" />
                        </button>
                    </div>
                )}

                <div className="w-full max-w-2xl flex flex-col items-center gap-6 px-8 py-12 rounded-2xl border border-line bg-surface/40">
                    <div className="w-16 h-16 rounded-2xl bg-accent/10 border border-accent-fg/20 flex items-center justify-center">
                        <AutoAwesomeIcon style={{ color: dsVar('accent-fg'), fontSize: 28 }} />
                    </div>

                    <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-fg m-0">No insights yet</h3>
                        <p className="text-sm text-fg-2 m-0 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif' }}>Run the workflow; your insights will appear here.</p>
                    </div>

                    {/* shrink-0 + whitespace-nowrap: without them the buttons shrink
                        below their content at narrow widths and swallow their padding. */}
                    <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
                        <button
                            onClick={handleConnectAndAnalyze}
                            className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 bg-accent text-on-accent rounded-xl font-bold text-xs uppercase tracking-wider border border-transparent hover:shadow-[0_0_20px_rgba(255,222,89,0.3)] transition-all cursor-pointer"
                        >
                            <PlayArrowIcon style={{ fontSize: 18 }} />
                            Run agent workflow
                        </button>
                        <button
                            onClick={handleCustomize}
                            disabled={isCustomizing}
                            className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 bg-transparent text-accent-fg rounded-xl font-bold text-xs uppercase tracking-wider border border-accent-fg/40 hover:border-accent-fg hover:bg-accent/5 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <TuneIcon style={{ fontSize: 18 }} />
                            {isCustomizing ? 'Opening…' : 'Customise'}
                        </button>
                    </div>

                    <p className="text-[11px] text-fg-muted m-0" style={{ fontFamily: '"Inter", sans-serif' }}>
                        Customise opens this agent's workflow in AI Studio
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
            <Sidebar activePage="hub" forceCollapsed={drawerOpen} />

            {/* Main Content */}
            <main className="flex-1 flex flex-col overflow-hidden bg-page">
                {/* Global Header Search */}
                <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10 m-0">
                    <div className="flex-1 max-w-2xl text-left">
                        <div className="relative group flex items-center justify-start h-full">
                            <SearchIcon className="absolute left-3 text-fg-muted group-focus-within:text-accent-fg transition-colors" />
                            <input className="w-full bg-surface border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent-fg placeholder:text-fg-muted text-fg outline-none" placeholder="Search..." type="text" />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 ml-8">
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <NotificationsIcon />
                        </button>
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <HelpIcon />
                        </button>
                        <button
                            className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                            onClick={handleUserMenuOpen}
                        >
                            <AccountCircleIcon />
                        </button>
                        <Menu
                            anchorEl={userMenuAnchorEl}
                            open={Boolean(userMenuAnchorEl)}
                            onClose={handleUserMenuClose}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    backgroundColor: dsVar('surface'),
                                    color: dsVar('fg-body'),
                                    border: `1px solid ${dsVar('line')}`,
                                    mt: 1
                                }
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    handleUserMenuClose();
                                    navigate('/access-control');
                                }}
                                sx={{ color: dsVar('fg-body'), gap: 1 }}
                            >
                                <AccountCircleIcon fontSize="small" />
                                Your account
                            </MenuItem>
                            <MenuItem onClick={handleSignOut} sx={{ color: dsVar('accent-fg'), gap: 1 }}>
                                <LogoutIcon fontSize="small" />
                                Sign out
                            </MenuItem>
                        </Menu>
                    </div>
                </header>

                {/* ── Title + Tabs bar: one row, or the tabs on a row of their own once a docked
                    drawer leaves the title under 16rem. The title keeps to one line either way. ── */}
                <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 pt-6 pb-4 shrink-0 text-left">
                    <div className="flex items-center gap-4 flex-1 min-w-0 basis-64">
                        <button
                            onClick={() => navigate(`/analytics/projects/${projectId}`)}
                            className="shrink-0 bg-transparent border border-line text-fg-soft hover:text-accent-fg hover:border-accent-fg/50 p-2 rounded-full transition-colors cursor-pointer flex items-center justify-center"
                        >
                            <ArrowBackIcon fontSize="small" />
                        </button>
                        <h2
                            className="text-3xl m-0 p-0 text-left font-bold min-w-0 truncate"
                            style={{ color: dsVar('accent-fg') }}
                            title={campaignName || undefined}
                        >
                            {campaignName || "Campaign Insights"}
                        </h2>
                    </div>

                    {/* Pill Toggle for Tabs */}
                    {!isAnalyzing && (
                        <div className="shrink-0 bg-surface p-1 rounded-full border border-line flex gap-1">
                            {INSIGHTS_TABS.map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => selectTab(tab.toLowerCase())}
                                    className={`px-8 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-none cursor-pointer ${activeTab === tab.toLowerCase()
                                        ? 'bg-accent text-on-accent'
                                        : 'text-fg-2 hover:text-fg-body bg-transparent'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Tab Content (fills remaining height) ── */}
                <div className="flex-1 overflow-hidden px-4 pb-4">
                    {activeTab === 'insights' && (
                        <div className={`flex h-full w-full ${chatPanel.yielded ? 'gap-0' : 'gap-10'}`}>
                            {/* Left Column: Chat Assistance — fills full height, and steps aside for a widened drawer */}
                            <div className="flex-none flex flex-col h-full" style={chatPanel.style}>
                                <ChatAssistance
                                    key={chatPanel.interactionId}
                                    {...chatPanel.chatProps}
                                    title="Investigation Assistant"
                                    contextData={{ campaignId, projectId }}
                                    onOpenReference={openReference}
                                    activeReferenceId={openGroup?.kind === 'insight' ? openGroup.insightId : null}
                                />
                            </div>

                            {/* Right Column: scrollable cards list */}
                            <div className="flex-1 min-w-0 flex flex-col h-full overflow-y-auto custom-scrollbar pr-1">
                                {loading ? (
                                    <div className="space-y-4 pt-2">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-24 bg-surface rounded-xl animate-pulse border border-line"></div>
                                        ))}
                                    </div>
                                ) : capsules.length > 0 ? (
                                    <div className="flex flex-col gap-4 pt-2 pb-4 flex-1">
                                        {capsules.map((capsule) => (
                                            <CapsuleInsightsCard
                                                key={capsule._id}
                                                campaignId={campaignId}
                                                capsule={capsule}
                                                isExpanded={expandedCapsuleId === capsule._id}
                                                onToggle={() => {
                                                    // One capsule is expanded at a time, so any toggle collapses the drawer's.
                                                    setOpenGroup(null);
                                                    setExpandedCapsuleId(expandedCapsuleId === capsule._id ? null : capsule._id);
                                                }}
                                                selectedGroupId={openGroup?.kind === 'group' && openGroup.capsuleId === capsule._id ? openGroup.groupIdentifier : null}
                                                onSelectGroup={(row, groupIdentifier) => setOpenGroup({
                                                    kind: 'group', capsuleId: capsule._id, groupIdentifier, groupName: row.groupName,
                                                    activityTemplateCode: capsule.activityTemplateCode || undefined,
                                                })}
                                                refreshKey={summaryRefresh[capsule._id] ?? 0}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                                        <QuickConnectFallback />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'run health' && (
                        <div className="h-full overflow-y-auto custom-scrollbar pt-2">
                            <RunHealthPanel campaignId={campaignId} flowCode={flowTemplateCode || null} />
                        </div>
                    )}

                    {activeTab === 'explainability' && (
                        <div className="h-full overflow-y-auto custom-scrollbar">
                            {renderExplainability()}
                        </div>
                    )}
                </div>
            </main>

            <InsightGroupDrawer
                campaignId={campaignId}
                group={openGroup}
                onClose={closeGroup}
                onReviewSubmitted={refreshAfterReview}
                showReasoningAction={reasoningActionFor}
                expanded={drawerWide}
                onToggleExpand={toggleDrawerWide}
            />
            <StageDetailDrawer
                stage={openStageIndex !== null ? explainabilityData[openStageIndex] ?? null : null}
                isLastStage={openStageIndex === explainabilityData.length - 1}
                onClose={closeStage}
                expanded={drawerWide}
                onToggleExpand={toggleDrawerWide}
            />
        </div>
    );
};

export default CampaignInsights;
